<template>
  <section class="fu-done" data-test="fu-admin-done-rail" aria-labelledby="fu-done-title">
    <div class="fu-done__head">
      <div class="fu-done__titlerow">
        <span class="fu-done__badge" aria-hidden="true"><fu-icon name="check" :size="14" :stroke="2.6" /></span>
        <h2 id="fu-done-title" class="fu-done__title">Done today</h2>
        <span class="fu-done__count" data-test="fu-done-count">{{ count }}</span>
      </div>
      <div class="fu-done__sub" data-test="fu-done-sub">{{ subText }}</div>

      <div v-if="goals && goals.show" class="fu-done__goals" data-test="fu-done-goals">
        <div class="fu-done__goals-row">
          <span class="fu-done__goals-label">Daily call goals</span>
          <span class="fu-done__goals-num" data-test="fu-done-goals-num">
            <strong>{{ goals.done }}</strong><span class="fu-done__goals-of"> / {{ goals.goal }}</span>
          </span>
        </div>
        <div
          class="fu-done__bar"
          role="progressbar"
          aria-label="Daily call goals"
          :aria-valuenow="goals.done"
          aria-valuemin="0"
          :aria-valuemax="goals.goal"
        >
          <div class="fu-done__bar-fill" :style="{ width: `${goals.pct}%` }"></div>
        </div>
        <div class="fu-done__goals-text" data-test="fu-done-goals-text">{{ goalText }}</div>
      </div>
    </div>

    <ul v-if="shown.length" class="fu-done__list">
      <li
        v-for="(d, i) in shown"
        :key="`${d.queueKey}-${i}`"
        class="fu-done__item"
        data-test="fu-done-item"
        :data-queue="d.queueKey"
      >
        <span class="fu-done__icon" aria-hidden="true"><fu-icon :name="d.icon" :size="15" /></span>
        <div class="fu-done__text">
          <div class="fu-done__name">{{ d.patient || 'Unnamed patient' }}</div>
          <div class="fu-done__what">{{ d.what }}</div>
        </div>
        <div class="fu-done__when">
          <span v-if="d.time" class="fu-done__time">{{ d.time }}</span>
          <span v-if="d.by" class="fu-done__avatar" :title="d.by.name" data-test="fu-done-avatar">{{ d.by.initials }}</span>
        </div>
      </li>
    </ul>
    <div v-else-if="!taskList.length" class="fu-done__empty" data-test="fu-done-empty">Follow-ups you log today will show up here.</div>

    <button
      v-if="hasMore || showAll"
      type="button"
      class="fu-done__more"
      data-test="fu-done-show-all"
      :aria-expanded="showAll ? 'true' : 'false'"
      @click="showAll = !showAll"
    >
      {{ showAll ? 'Show fewer' : `Show all ${followupCount}` }}
      <fu-icon name="chev" :size="14" :class="{ 'fu-done__chev--up': showAll }" />
    </button>

    <div v-if="taskList.length" class="fu-done__tasks" data-test="fu-done-tasks">
      <div class="fu-done__group" data-test="fu-done-tasks-head">Tasks · {{ taskList.length }}</div>
      <ul class="fu-done__list">
        <li v-for="t in shownTasks" :key="`task-${t.id}`" class="fu-done__task-li">
          <button
            type="button"
            class="fu-done__item fu-done__item--task"
            data-test="fu-done-task"
            :data-task-id="t.id"
            :title="t.text"
            @click="$emit('open-task', t.task)"
          >
            <span class="fu-done__icon" aria-hidden="true"><fu-icon name="check" :size="15" /></span>
            <span class="fu-done__text">
              <span class="fu-done__name">{{ t.text }}</span>
              <span class="fu-done__what">Task</span>
            </span>
            <span class="fu-done__when">
              <span v-if="t.by" class="fu-done__avatar" :title="t.by.name" data-test="fu-done-avatar">{{ t.by.initials }}</span>
            </span>
          </button>
        </li>
      </ul>
      <button
        v-if="tasksHasMore || showAllTasks"
        type="button"
        class="fu-done__more"
        data-test="fu-done-tasks-show-all"
        :aria-expanded="showAllTasks ? 'true' : 'false'"
        @click="showAllTasks = !showAllTasks"
      >
        {{ showAllTasks ? 'Show fewer tasks' : `Show all ${taskList.length} tasks` }}
        <fu-icon name="chev" :size="14" :class="{ 'fu-done__chev--up': showAllTasks }" />
      </button>
    </div>
  </section>
</template>

<script>
/**
 * AdminDoneRail — the Admin "Today" page's "Done today" card (Admin redesign Phase A1,
 * 2026-10-06; design "Today 3a", right column). Presentational: everything comes in as props.
 *
 *   <admin-done-rail :rail="doneRail(split.done, { userID })" :goals="callGoals(everyoneDone, goalValues, visibleQueueKeys)"
 *     :employees="employees" :user-i-d="userID" :time-zone="siteTimeZone" />
 *
 *   rail       src/utils/followups/dashboard.js doneRail() — items newest first, count, teammates…
 *   goals      dashboard.js callGoals() — the box shows only when `show` (both goals ≤ 0 ⇒ hidden)
 *   employees  state.customer.employees — the avatar initials of each item's AssignedToEmpID (the
 *              query has no worked-by column; AssignedToEmpID is the proxy, see dashboard.js)
 *   userID     who "You" is in "You and N teammates"
 *   timeZone   the site's GENERAL_TIMEZONE; times are shown in it
 *   sourceZone the zone the raw 'YYYY-MM-DD HH:mm:ss' times are written in. teamio.tasks
 *              *StatusUpdatedAt has the same value as UpdatedAt, and the RDS clock is UTC — so UTC.
 *              A value WITH an offset is read as written.
 *
 *   tasks      the tasks finished today (Phase 7, AdminPage fuDoneTasks over
 *              tasks/subscribeCompletedTasksToday): [{ id, text, completedByEmpID, task }]
 *
 * Shows the first 8 follow-ups, then "Show all N" (and "Show fewer"). Tasks are their own group
 * BELOW the follow-ups ("Tasks · N", first 5 then "Show all N tasks"): check icon, the cleaned text,
 * "Task", the completer's initials — NO time (the store keeps no real completion time: tasks/
 * updateTask stamps completedAt as the start of the day). A task is a button → `open-task(task)`.
 * The header count is follow-ups + tasks; "You and N teammates" counts distinct people across
 * both; "since H:MM" is the earliest FOLLOW-UP time (omitted when only tasks are done). The call
 * goals never count tasks. Does not use src/mixin.js (its `employees` computed would replace the prop).
 */
import moment from 'moment-timezone';
import FuIcon from './FuIcon.vue';
import { QUEUE_ICONS, DASHBOARD_TYPE_LABELS } from '@/utils/followups/rowModel.js';

const DONE_RAIL_PAGE = 8;
const DONE_TASKS_PAGE = 5;

const OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/i;

function nameInitials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

export default {
  name: 'AdminDoneRail',
  components: { FuIcon },
  props: {
    rail: { type: Object, default: () => ({ items: [], count: 0, teammates: [], teammateCount: 0, includesMe: false, earliestAt: null }) },
    goals: { type: Object, default: null },
    employees: { type: Array, default: () => [] },
    userID: { type: [String, Number], default: null },
    timeZone: { type: String, default: 'America/New_York' },
    sourceZone: { type: String, default: 'UTC' },
    tasks: { type: Array, default: () => [] },
  },

  data() {
    return { showAll: false, showAllTasks: false };
  },

  computed: {
    items() {
      return (this.rail && Array.isArray(this.rail.items)) ? this.rail.items : [];
    },
    followupCount() {
      return this.rail && typeof this.rail.count === 'number' ? this.rail.count : this.items.length;
    },
    taskList() {
      return (Array.isArray(this.tasks) ? this.tasks : [])
        .filter(t => t && t.id !== null && t.id !== undefined && t.id !== '')
        .map(t => ({
          id: t.id,
          text: String(t.text || '').trim() || 'Untitled task',
          by: this.employeeOf(t.completedByEmpID),
          empID: t.completedByEmpID === null || t.completedByEmpID === undefined ? null : String(t.completedByEmpID).trim(),
          task: t.task || t,
        }));
    },
    count() {
      return this.followupCount + this.taskList.length;
    },
    /** Distinct people across the done follow-ups AND tasks: { includesMe, teammates }. */
    people() {
      const me = this.userID === null || this.userID === undefined ? '' : String(this.userID).trim();
      const r = this.rail || {};
      let includesMe = !!r.includesMe;
      const mates = (Array.isArray(r.teammates) ? r.teammates : []).map(String);
      this.taskList.forEach((t) => {
        if (!t.empID) return;
        if (me && t.empID === me) includesMe = true;
        else if (mates.indexOf(t.empID) === -1) mates.push(t.empID);
      });
      return { includesMe, teammates: mates.length };
    },
    subText() {
      if (!this.count) return 'Nothing logged yet today.';
      const n = this.people.teammates;
      const mates = `${n} teammate${n === 1 ? '' : 's'}`;
      let who;
      if (this.people.includesMe) who = n ? `You and ${mates}` : 'You';
      else who = n ? mates : 'Your team';
      const since = this.followupCount ? this.formatTime(this.rail.earliestAt) : '';
      return since ? `${who} since ${since}` : who;
    },
    tasksHasMore() {
      return this.taskList.length > DONE_TASKS_PAGE;
    },
    shownTasks() {
      return this.showAllTasks ? this.taskList : this.taskList.slice(0, DONE_TASKS_PAGE);
    },
    goalText() {
      const g = this.goals || {};
      if (!g.left) return 'Today\'s call goals are hit.';
      return `${g.left} more recall or treatment call${g.left === 1 ? '' : 's'} to hit today's goals.`;
    },
    hasMore() {
      return this.items.length > DONE_RAIL_PAGE;
    },
    shown() {
      const list = this.showAll ? this.items : this.items.slice(0, DONE_RAIL_PAGE);
      return list.map(it => ({
        queueKey: it.queueKey,
        patient: it.patient,
        icon: QUEUE_ICONS[it.queueKey] || 'list',
        what: [DASHBOARD_TYPE_LABELS[it.queueKey] || 'Follow-up', it.status].filter(Boolean).join(' · '),
        time: this.formatTime(it.at),
        by: this.employeeOf(it.empID),
      }));
    },
  },

  methods: {
    /** A raw DB time → 'h:mm A' in the site's zone; '' when unreadable. */
    formatTime(value) {
      if (value === null || value === undefined || value === '') return '';
      let m;
      if (value instanceof Date || typeof value === 'number') m = moment(value);
      else if (OFFSET_RE.test(String(value))) m = moment.parseZone(String(value));
      else m = moment.tz(String(value).replace(' ', 'T'), this.sourceZone || 'UTC');
      if (!m || !m.isValid()) return '';
      return (this.timeZone ? m.clone().tz(this.timeZone) : m).format('h:mm A');
    },
    employeeOf(id) {
      if (id === null || id === undefined || id === '') return null;
      const e = (this.employees || []).find(x => String(x.EmployeeID) === String(id));
      if (!e) return null;
      return { name: e.Name, initials: nameInitials(e.Name) || '?' };
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-done {
  @include fu-card;
  @include fu-font-base;
  overflow: hidden;
  min-width: 0;
}

.fu-done__head {
  padding: $fu-space-4 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-done__titlerow {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fu-done__badge {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: $fu-success;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.fu-done__title {
  @include fu-font-display;
  margin: 0;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.02em;
  color: $fu-text;
}

.fu-done__count {
  @include fu-font-display;
  @include fu-tabular;
  margin-left: auto;
  font-weight: 800;
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.03em;
  color: $fu-success;
}

.fu-done__sub {
  font-size: 12.5px;
  color: $fu-text-muted;
  margin-top: 6px;
}

.fu-done__goals {
  margin-top: 14px;
  padding: $fu-space-3;
  border-radius: $fu-radius-lg;
  background: $fu-neutral-25;
  border: 1px solid $fu-border;
}

.fu-done__goals-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: $fu-space-2;
  font-size: 12.5px;
}

.fu-done__goals-label {
  font-weight: $fu-fw-bold;
  color: $fu-neutral-600;
}

.fu-done__goals-num {
  @include fu-tabular;
  strong { font-weight: $fu-fw-bold; color: $fu-text; }
}

.fu-done__goals-of {
  color: $fu-text-subtle;
  font-weight: $fu-fw-semibold;
}

.fu-done__bar {
  height: 6px;
  border-radius: 6px;
  background: $fu-neutral-100;
  overflow: hidden;
  margin-top: $fu-space-2;
}

.fu-done__bar-fill {
  height: 100%;
  border-radius: 6px;
  background: $fu-brand;
  transition: width 0.3s ease;
}

.fu-done__goals-text {
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: $fu-space-2;
}

.fu-done__list {
  list-style: none;
  margin: 0 0 -1px;
  padding: 0;
}

.fu-done__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-done__icon {
  width: 28px;
  height: 28px;
  border-radius: $fu-radius-md;
  background: $fu-success-subtle;
  color: $fu-success;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.fu-done__text {
  min-width: 0;
  flex: 1;
}

.fu-done__name {
  @include fu-ellipsis;
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.fu-done__what {
  @include fu-ellipsis;
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: 1px;
}

.fu-done__when {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  flex: none;
}

.fu-done__time {
  font-size: 12px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
  white-space: nowrap;
}

.fu-done__avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $fu-brand-50;
  color: $fu-brand-700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: $fu-fw-bold;
}

.fu-done__empty {
  padding: 28px 18px;
  text-align: center;
  font-size: 13px;
  color: $fu-text-subtle;
}

.fu-done__more {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: $fu-space-3 18px;
  border-top: 1px solid $fu-border;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
  &:hover { background: $fu-neutral-25; }
}

.fu-done__chev--up {
  transform: rotate(180deg);
}

// ---- Tasks finished today (their own group under the follow-ups) ----
.fu-done__tasks {
  border-top: 1px solid $fu-border;
}

.fu-done__group {
  @include fu-eyebrow;
  padding: $fu-space-3 18px 6px;
}

.fu-done__task-li {
  min-width: 0;
}

.fu-done__item--task {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background $fu-dur-fast $fu-ease-out;
  &:hover { background: $fu-neutral-25; }
  .fu-done__text,
  .fu-done__name,
  .fu-done__what { display: block; }
}
</style>
